import {
  EAccessCodeStatus,
  ManagerTypeOrder,
} from 'features/AccessManagement/hooks/useOrganizationFormData'

import { ManagerViewDataByType } from 'features/AccessManagement/hooks/useOrganizationFormData'
import { organizationSingleNumbers } from 'features/AccessManagement/models/AccessManagement.model'
import { AccessCode, BaseAccessCode, EAccessCodeType } from 'types'
import { formatDateAccessCode } from 'utils/date'
import { FormType } from './AccessCode.scheme'

export const prepareNewAccessCode = ({
  values,
  organizationId,
  accessCode,
  singleLetterOrg,
  managerIds,
}: {
  values: FormType
  organizationId: number
  accessCode: BaseAccessCode
  singleLetterOrg: string
  managerIds: Record<string, number>
}) => {
  if (organizationId) {
    accessCode.organizationId = organizationId
  } else {
    accessCode = {
      ...accessCode,
      organizationName: values.organizationName,
      organizationType: values.organizationType,
      organizationTwoDigits: values.twoDigitsOrg,
      organizationSingleLetter: singleLetterOrg,
    }
  }

  ManagerTypeOrder.forEach((managerType) => {
    const prefix = ManagerViewDataByType[managerType].backendField as
      | 'owner'
      | 'regionalManager'
      | 'facilityManager'
      | 'supervisor'
    if (managerIds[managerType]) {
      accessCode[`${prefix}Id`] = managerIds[managerType]
    } else {
      if (values[`${managerType}FirstName`]) {
        accessCode[`${prefix}FirstName`] = values[`${managerType}FirstName`]
      }
      if (values[`${managerType}LastName`]) {
        accessCode[`${prefix}LastName`] = values[`${managerType}LastName`]
      }
      if (values[`${managerType}Email`]) {
        accessCode[`${prefix}Email`] = values[`${managerType}Email`]
      }
    }
  })
  return accessCode
}

export const prepareBaseAccessCode = (
  values: FormType,
  codeType: EAccessCodeType,
  code?: string,
): BaseAccessCode => {
  return {
    codeType: codeType,
    code: code || values.code || '',
    reason: values.reason || '',
    effectiveDate: values.effectiveDate ? formatDateAccessCode(values.effectiveDate) : null,
    expiryDate: values.expiryDate ? formatDateAccessCode(values.expiryDate) : null,
    specificState: values.state || '',
    specificUnitName: values.specificUnitName || '',
    specificTwoDigits: values.twoDigitsSpecific || '',
    duration: values.duration || '',
    numberOfUsers: Number(values.numberOfUsers),
  }
}

export const prepareDraftAccessCode = (
  values: FormType,
  codeType: EAccessCodeType,
  code: string,
): Partial<AccessCode> => {
  return {
    ...prepareBaseAccessCode(values, codeType, code),
    organizationName: values.organizationName,
    organizationType: values.organizationType,
    organizationTwoDigits: values.twoDigitsOrg,
    organizationSingleLetter: getSingleLetterOrg(values),
    organizationId: Number(values.organizationId) || undefined,
    ownerId: Number(values.ownerId) || undefined,
    regionalManagerId: Number(values.regionalManagerId) || undefined,
    facilityManagerId: Number(values.facilityManagerId) || undefined,
    supervisorId: Number(values.supervisorId) || undefined,
    ownerFirstName: values.ownerFirstName || '',
    ownerLastName: values.ownerLastName || '',
    ownerEmail: values.ownerEmail || '',
    regionalManagerFirstName: values.regionalFirstName || '',
    regionalManagerLastName: values.regionalLastName || '',
    regionalManagerEmail: values.regionalEmail || '',
    facilityManagerFirstName: values.facilityFirstName || '',
    facilityManagerLastName: values.facilityLastName || '',
    facilityManagerEmail: values.facilityEmail || '',
    supervisorFirstName: values.supervisorFirstName || '',
    supervisorLastName: values.supervisorLastName || '',
    supervisorEmail: values.supervisorEmail || '',
    status: EAccessCodeStatus.draft,
  }
}

export const getSingleLetterOrg = (values: FormType): string => {
  return organizationSingleNumbers.find((it) => it.value === values.organizationType)
    ?.label as string
}
